import Vue from 'vue';
import { Getter } from 'vuex-class';
import Component from 'vue-class-component';
import { HlaAntibodyTag } from '@/store/labs/types';

@Component
export class HlaAntibodySortMixin extends Vue {

  @Getter('parseHlaAntibodyTag', { namespace: 'labs' }) parseHlaAntibodyTag!: (rawInput: string) => HlaAntibodyTag;

  // Sort antibody tags alphabetically by locus, then numerically by allele group/protein
  public sortAntibodies(antibodies: string[]): string[] {
    return (antibodies || []).slice().sort((a: string, b: string) => {
      const parsedA = this.parseHlaAntibodyTag(a);
      const parsedB = this.parseHlaAntibodyTag(b);
      const locusA = parsedA.locusText || '';
      const locusB = parsedB.locusText || '';
      if (locusA !== locusB) {
        return locusA.localeCompare(locusB);
      }
      const alleleDiff = this.alleleSortValue(parsedA.allele) - this.alleleSortValue(parsedB.allele);
      if (alleleDiff !== 0) {
        return alleleDiff;
      }
      // Alpha portion is identical - for alpha-beta antibodies, fall back to comparing the beta portion
      const betaLocusA = parsedA.betaLocusText || '';
      const betaLocusB = parsedB.betaLocusText || '';
      if (betaLocusA !== betaLocusB) {
        return betaLocusA.localeCompare(betaLocusB);
      }
      return this.alleleSortValue(this.betaAllele(parsedA)) - this.alleleSortValue(this.betaAllele(parsedB));
    });
  }

  // Extract the beta portion's allele (e.g. '06:30') from a parsed alpha-beta antibody tag
  private betaAllele(parsed: HlaAntibodyTag): string {
    const betaMolecular = (parsed.alphaBetaSpecific || [])[1] || '';
    return betaMolecular.split('*')[1] || '';
  }

  // Convert an allele (e.g. '01:02' or '13:XX') into a comparable number for numeric sorting.
  // Allele-specific antibodies (protein present) always sort after group-only antibodies,
  // regardless of group number, then numerically by group and protein within each bucket.
  private alleleSortValue(allele?: string): number {
    const [group, protein] = (allele || '').split(':');
    const groupNumber = parseInt(group, 10) || 0;
    const isAlleleSpecific = !!protein && protein !== 'XX';
    const proteinNumber = isAlleleSpecific ? (parseInt(protein, 10) || 0) : 0;
    const alleleSpecificOffset = isAlleleSpecific ? 10000 : 0;
    return alleleSpecificOffset + groupNumber + (proteinNumber / 1000);
  }
}
